import { SECRET_LEVEL_LABELS } from '@infras/shared-types';
import type { SecretLevel } from '@infras/shared-types';
import { AvatarUpload } from '../components/AvatarUpload';
import { McpTokens } from '../components/McpTokens';
import { NotificationCenter } from '../components/NotificationCenter';
import { useMe } from '../lib/useMe';

/**
 * Your own account: identity + what you can see, then your MCP tokens.
 *
 * Was `/settings/profile` and `/settings/access` — two tabs for one person's own
 * page, when the only reason to visit either is "what am I, and what token do I
 * hold". They are one page now; `/settings/access` redirects here.
 *
 * Appearance is deliberately NOT duplicated here: it already lives in the
 * account menu in the top bar, one click from anywhere, and two switchers sharing
 * one localStorage key is how they drift.
 */
export function AccountPage() {
  const { me, loading, error } = useMe();

  return (
    <div className="page stack">
      <div className="detail-head">
        <h2>Account</h2>
      </div>

      {error && <p className="error">{error.message}</p>}
      {loading && !me && (
        <div className="skeleton-row-group">
          {[0, 1, 2].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      )}

      {me && (
        <>
          <section>
            <h3 className="section">Profile photo</h3>
            <AvatarUpload me={me} />
          </section>

          <section>
            <h3 className="section">Identity</h3>
            <div className="attrs">
              <div className="attrs__row">
                <div className="attrs__k">Email</div>
                <div className="attrs__v">{me.email}</div>
              </div>
              <div className="attrs__row">
                <div className="attrs__k">App role</div>
                <div className="attrs__v">
                  <span className="tag-badge">{me.role.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="attrs__row">
                <div className="attrs__k">Secret visibility</div>
                <div className="attrs__v">
                  {me.allowedLevels.length ? (
                    me.allowedLevels.map((l) => (
                      <span key={l} className="tag-badge" style={{ marginRight: 4 }}>
                        {SECRET_LEVEL_LABELS[l as SecretLevel] ?? l} · {l}
                      </span>
                    ))
                  ) : (
                    <span className="muted">none — onboarding only</span>
                  )}
                </div>
              </div>
              <div className="attrs__row">
                <div className="attrs__k">staff-auth roles</div>
                <div className="attrs__v">
                  {me.roles.length ? (
                    me.roles.map((r) => (
                      <span key={r} className="tag-badge" style={{ marginRight: 4 }}>
                        {r}
                      </span>
                    ))
                  ) : (
                    <span className="muted">none</span>
                  )}
                </div>
              </div>
            </div>
            <p className="muted text-2xs" style={{ marginTop: 8 }}>
              Roles are assigned by a root user in Administration → Users &amp; roles. Ask there if
              you need more than you have.
            </p>
          </section>

          <section>
            <h3 className="section">MCP tokens</h3>
            <McpTokens />
          </section>

          {/* Renders its own <section> heading, because it removes the WHOLE
              section when push is unconfigured for the environment — a heading
              here would survive that and leave an empty band. */}
          <NotificationCenter />
        </>
      )}
    </div>
  );
}
